import * as React from 'react';

import Pusher from 'pusher-js';

import type {InboxChannelEventFragment as ChannelEvent, ThreadType} from 'nutshell-graphql-types';

import {ThreadSummary} from '../../../../../../ui/inbox/shared-components/thread-list-sidebar/thread-summary';

import {ListViewPaginated} from '../../list-view/list-view-paginated';
import {LoadingPage} from '../../loading-page/loading-page';

import {renderChannelEvent} from '../helpers';
import {EmptyState} from '../sidebar/empty-state';
import type {Recipient} from '../types';

import {TypingIndicator} from './typing-indicator/typing-indicator';

import styles from './channel-container.module.css';

type Props = {
    isLoading: boolean;
    recipient: Recipient;
    channelEvents: ChannelEvent[];
    onResendMessage: (messageId: string) => Promise<any>;
    onDeleteMessage: (messageId: string) => Promise<any>;
    isStartingNewThread?: boolean;
    fetchMoreRows: () => any;
    hasNextPage: boolean;
    preventResend: boolean;
    sessionId: string;
    threadSummary?: string | null;
    shouldScrollToBottomOnInitialLoad?: boolean;
    shouldShowTimestampPopover: boolean;
    isSidebar?: boolean;
    threadType?: ThreadType;
};

export function ChannelEvents(props: Props) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isTyping, setIsTyping] = React.useState(false);

    // Trickery to make sure we don't flash the empty state for a split second between the loading state and the events showing
    React.useEffect(() => {
        if (props.isLoading && !isLoading) {
            setIsLoading(true);
        }

        if (!props.isLoading && isLoading) {
            setTimeout(() => {
                setIsLoading(false);
            }, 100);
        }
    }, [props.isLoading, isLoading]);

    React.useEffect(() => {
        if (!props.sessionId) {
            return;
        }

        const pusher = new Pusher(NutClientConfig.pusherConfig.key, {
            cluster: NutClientConfig.pusherConfig.cluster,
            // @ts-expect-error
            encrypted: true,
        });

        const channel = pusher.subscribe(props.sessionId);

        // @ts-expect-error
        channel.bind('visitor-typing', (data) => {
            if (data && data.isTyping) {
                setIsTyping(true);

                setTimeout(() => {
                    setIsTyping(false);
                }, 5000);
            }
        });

        return () => {
            pusher.disconnect();
        };
    }, [props.sessionId]);

    // Find the last read message
    const findLastReadMessageIndex = () => {
        // Find the message with the most recent readTime
        let lastIndex = -1;

        props.channelEvents.forEach((current, index) => {
            if (
                current.payload.__typename === 'Message' &&
                current.payload.isOutbound &&
                current.payload.readTime
            ) {
                lastIndex = index;
            }
        });

        return lastIndex;
    };

    const lastReadMessageIndex = findLastReadMessageIndex();

    // Function to determine spacing based on whether the next message is from the same direction
    const getItemSpacing = (channelEvent: ChannelEvent, index: number) => {
        const nextChannelEvent =
            index < props.channelEvents.length - 1 ? props.channelEvents[index + 1] : null;

        if (!nextChannelEvent) {
            return 8;
        }

        if (
            channelEvent.payload.__typename !== 'Message' ||
            nextChannelEvent.payload.__typename !== 'Message'
        ) {
            return 16;
        }

        const isSameDirection =
            channelEvent.payload.isOutbound === nextChannelEvent.payload.isOutbound;

        return isSameDirection ? 8 : 16;
    };

    const shouldShowSummary =
        props.threadSummary && (!props.isLoading || props.channelEvents.length > 0);

    const headerComponent = shouldShowSummary && (
        <div className={styles['thread-summary-container']}>
            {/* @ts-expect-error - props.threadSummary is checked above */}
            <ThreadSummary summary={props.threadSummary} shouldTruncateSummary={false} />
        </div>
    );

    return (
        <ListViewPaginated
            hasNextPage={props.hasNextPage}
            onFetchMoreRows={props.fetchMoreRows}
            isLoading={isLoading}
            invertScrolling={true}
            getItemSpacing={getItemSpacing}
            listPadding={16}
            alignCenter={true}
            maxWidth={800}
            shouldScrollToBottomOnInitialLoad={props.shouldScrollToBottomOnInitialLoad}
            headerComponent={headerComponent}
            specialStates={[
                {
                    shouldRender: () =>
                        !isLoading &&
                        props.channelEvents.length === 0 &&
                        !props.isStartingNewThread,
                    component: (
                        <EmptyState
                            imageSrc={'no-messages'}
                            text={`You haven't messaged ${props.recipient.name} yet. Start a new conversation below!`}
                        />
                    ),
                },
                {
                    shouldRender: () => Boolean(props.isStartingNewThread),
                    component: <EmptyState imageSrc={'no-messages'} text='Send a message below' />,
                },
                {
                    shouldRender: () => isLoading && props.channelEvents.length === 0,
                    component: (
                        <div className='mt-64'>
                            <LoadingPage />
                        </div>
                    ),
                },
            ]}
            collection={props.channelEvents}
            // @ts-expect-error
            renderRow={(channelEvent: ChannelEvent, index: number) => {
                const nextChannelEvent =
                    index < props.channelEvents.length - 1 ? props.channelEvents[index + 1] : null;

                const isNextMessageFromDifferentActor = Boolean(
                    nextChannelEvent &&
                        nextChannelEvent.payload &&
                        nextChannelEvent.payload.__typename === 'Message' &&
                        channelEvent.payload.__typename === 'Message' &&
                        nextChannelEvent.payload.isOutbound !== channelEvent.payload.isOutbound
                );

                const isNextChannelEventTypeMessage = Boolean(
                    nextChannelEvent && nextChannelEvent.payload.__typename === 'Message'
                );

                const isLastReadMessage = index === lastReadMessageIndex;

                return renderChannelEvent({
                    channelEvent,
                    recipient: props.recipient,
                    onResendMessage: props.onResendMessage,
                    onDeleteMessage: props.onDeleteMessage,
                    preventResend: props.preventResend,
                    shouldShowAvatar:
                        isNextMessageFromDifferentActor || !isNextChannelEventTypeMessage,
                    isLastEvent: index === props.channelEvents.length - 1,
                    isLastReadMessage,
                    shouldShowTimestampPopover: props.shouldShowTimestampPopover,
                    isSidebar: props.isSidebar,
                    threadType: props.threadType,
                });
            }}
            footerComponent={isTyping && <TypingIndicator />}
        />
    );
}
